import { motion } from "framer-motion";
import { MdCheckCircle } from "react-icons/md";

interface SavedSuccessfulPanelProps {
  title: string;
  paragraphs: string[];
  createText: string;
  onCreateAnother: () => void;
  onClose: () => void;
  includeBackground?: boolean;
}

/**
 * Definition of the variants of this component
 */
const variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: {
    opacity: 0,
    transition: { duration: 0.1, ease: "easeOut" },
  },
};

const variantsItems = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  exit: {
    opacity: 0,
    scale: 0.9,
    transition: { duration: 0.1, ease: "easeOut" },
  },
};

const SavedSuccessfulPanel: React.FC<SavedSuccessfulPanelProps> = ({
  title,
  paragraphs,
  createText,
  onCreateAnother,
  onClose,
  includeBackground = true,
}) => {
  return (
    <>
      {includeBackground && (
        <motion.div
          key="saved-successful-pop-up-background"
          className="fixed inset-0 w-full h-full bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] z-20 flex flex-col flex-grow"
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.1, ease: "easeOut" }}
          onClick={onClose}
        />
      )}
      <motion.div
        key="saved-successful-pop-up-main"
        className="fixed w-[440px] h-fit left-0 right-0 ml-auto mr-auto p-6 bottom-5 bg-white rounded-3xl z-30 overflow-hidden"
        variants={variantsItems}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <div className="inline-flex w-full max-h-[80vh] flex-col flex-1 justify-start items-center gap-10 overflow-y-auto custom-scroll">
          <div className="self-stretch px-3.5 py-9 justify-between items-start inline-flex">
            <div className="w-8 h-8 p-[7.21px] opacity-0 justify-center items-center flex" />
            <MdCheckCircle className="w-[108px] h-[108px] fill-success-500" />
            <div className="w-8 h-8 p-[7.21px] opacity-0 justify-center items-center flex" />
          </div>
          <div className="w-full h-fit flex-col justify-start items-start gap-6 flex">
            <div className="self-stretch h-fit flex-col justify-start items-start gap-6 flex">
              <div className="self-stretch h-7 flex-col justify-start items-start gap-3 flex">
                <div className="self-stretch justify-start items-start gap-2 inline-flex">
                  <div className="grow shrink basis-0 text-neutral-700 text-[23px] font-semibold font-sans leading-7">
                    {title}
                  </div>
                </div>
              </div>
              {paragraphs &&
                paragraphs.length > 0 &&
                paragraphs.map((paragraph: string, index: number) => (
                  <div
                    key={index}
                    className="self-stretch justify-start items-start gap-2 inline-flex"
                  >
                    <div className="grow shrink basis-0 text-neutral-700 text-base font-normal font-sans leading-7">
                      {paragraph}
                    </div>
                  </div>
                ))}
            </div>
            <div
              className="self-stretch h-[46px] px-5 py-3 bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 rounded justify-center items-center gap-2 inline-flex cursor-pointer"
              onClick={onCreateAnother}
            >
              <div className="text-white text-lg font-semibold font-sans leading-snug select-none">
                {createText}
              </div>
            </div>
            <div className="self-stretch h-[46px] px-5 py-3 rounded justify-center items-center gap-2 inline-flex">
              <div
                className="text-secondary-600 hover:underline text-lg font-semibold font-sans leading-snug cursor-pointer select-none"
                onClick={onClose}
              >
                Close
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default SavedSuccessfulPanel;
